import {css, StyleSheet} from 'aphrodite';
import {DragEventHandler} from 'react';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NODE_NAME_HEIGHT,
  ProcessFlowchartInteractiveNode,
  ProcessFlowchartNodeHeight,
  ProcessFlowchartNodeWidth,
  ProcessFlowchartStaticNode,
  ProcessFlowchartUIOnlyNodeType,
  ROUTER_AND_END_NODE_NAME_MAX_LENGTH,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {END_NODE_ICON} from 'processes/modals/router-config/RouterConfigUtils';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ellipsize} from 'util/NameUtil';

export function getNodeContents(
  data: ProcessFlowchartStaticNode | ProcessFlowchartInteractiveNode,
  nodeClass: string,
  draggable: boolean,
  children?: React.ReactNode,
  onDragStart?: DragEventHandler,
  onDragEnd?: DragEventHandler,
  isStatic?: boolean,
  nodeName?: (name: string) => JSX.Element
): JSX.Element {
  if (data.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    return (
      <div>
        <Popover
          interaction={FloatingInteraction.HOVER}
          placement={FloatingPlacement.TOP_END}
          renderContent={() => data.name}
          isDisabled={data.name == null || data.name.length <= ROUTER_AND_END_NODE_NAME_MAX_LENGTH}
        >
          <span className={css(styles.nodeNameWrapper)}>
            {data.name && nodeName
              ? nodeName(ellipsize(data.name, ROUTER_AND_END_NODE_NAME_MAX_LENGTH))
              : null}
          </span>
        </Popover>

        <div draggable={draggable} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className={nodeClass}>
            <div className={css(styles.diamondNodeContents)}>
              <div className={css(styles.diamondNodeHandlePositioner)}>{children}</div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (data.type === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    return (
      <div>
        <span className={css(styles.nodeNameWrapper)}>
          {data.name && nodeName ? nodeName(ellipsize(data.name, ROUTER_AND_END_NODE_NAME_MAX_LENGTH)) : null}
        </span>
        <div className={nodeClass} draggable={draggable} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <BenchlingIcon className={css(styles.pillIcon)} icon={END_NODE_ICON} />
          {children}
        </div>
      </div>
    );
  } else if (data.type === ProcessFlowchartUIOnlyNodeType.COLLAPSED_NODE) {
    let numNodesRepresented: number;
    if (data.numNodesRepresented != null) {
      numNodesRepresented = data.numNodesRepresented;
    } else {
      const parsedNumber = parseInt(data.name, 10);
      numNodesRepresented = Number.isNaN(parsedNumber) ? 0 : parsedNumber;
    }
    return (
      <Popover
        interaction={FloatingInteraction.HOVER}
        placement={FloatingPlacement.TOP_END}
        renderContent={() =>
          `${numNodesRepresented} ${pluralize(numNodesRepresented, 'node')} without tasks ${pluralize(
            numNodesRepresented,
            'is',
            'are'
          )} hidden from view`
        }
      >
        <div className={nodeClass} draggable={false} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          {nodeName && nodeName(`${numNodesRepresented}`)}
          {children}
        </div>
      </Popover>
    );
  } else {
    return (
      <div className={nodeClass} draggable={draggable} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        {data.name && nodeName && nodeName(data.name)}
        {children}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  diamondNodeBuffer: {
    width: ProcessFlowchartNodeWidth.DIAMOND,
    height: ProcessFlowchartNodeHeight.DIAMOND,
  },
  diamondNodeContents: {
    transform: 'rotate(45deg)',
    position: 'relative',
  },
  diamondNodeHandlePositioner: {
    height: ProcessFlowchartNodeHeight.DIAMOND,
  },
  nodeNameWrapper: {
    position: 'relative',
    display: 'block',
    width: '200%',
    overflow: 'visible',
    left: '-50%',
    lineHeight: `${NODE_NAME_HEIGHT}px`,
  },
  pillIcon: {
    fontSize: 15,
    color: GRAY_DARKER,
  },
});
